/**
 * Métodos de pagamento usados no Contas a Pagar.
 *
 * `method_payment` é gravado como slug no banco, enquanto o cadastro do
 * fornecedor ("Aceita pagamentos em:") guarda ids da tabela `payments`. O mapa
 * abaixo liga os dois — sempre pelo id, nunca pelo nome, porque o seed grava
 * "Pix" e a interface do fornecedor já mostrou "PIX" em alguns pontos.
 */

export const PAYMENT_METHOD_OPTIONS = [
  { value: "pix", label: "Pix" },
  { value: "boleto", label: "Boleto" },
  { value: "especie", label: "Espécie" },
  { value: "cartao", label: "Cartão" },
  { value: "transferencia", label: "Transferência" },
];

// ids conforme database/seeds/PaymentSeeder.js no backend
export const PAYMENT_ID_TO_SLUG = {
  1: "especie",
  2: "transferencia",
  3: "boleto",
  4: "cartao",
  5: "pix",
};

export const METHOD_LABEL = {
  pix: "PIX",
  boleto: "Boleto",
  especie: "Espécie",
  cartao: "Cartão",
  transferencia: "Transferência",
};

export const ACCOUNT_TYPE_LABEL = {
  cc: "Conta Corrente",
  cp: "Conta Poupança",
};

export const ACCOUNT_OWNERSHIP_LABEL = {
  cpf: "Pessoa Física",
  cnpj: "Pessoa Jurídica",
};

export const PIX_KEY_TYPE_OPTIONS = [
  { value: "cpf", label: "CPF" },
  { value: "cnpj", label: "CNPJ" },
  { value: "email", label: "E-mail" },
  { value: "phone", label: "Telefone" },
  { value: "random", label: "Chave aleatória" },
];

export const PIX_KEY_TYPE_LABEL = {
  cpf: "CPF",
  cnpj: "CNPJ",
  email: "E-mail",
  phone: "Telefone",
  random: "Chave aleatória",
};

/**
 * Métodos que o fornecedor aceita, no formato de options do Slct.
 * Fornecedor sem nenhum método cadastrado libera a lista completa (não travar
 * o gestor por causa de cadastro incompleto), e `keepValue` mantém na lista um
 * método já salvo no título mesmo que o fornecedor não o aceite mais — sem
 * isso o Slct ficaria vazio e reprovaria na validação de obrigatórios.
 */
export function supplierMethodOptions(supplier, options = {}) {
  const { keepValue } = options;

  const _accepted = (supplier?.payment || [])
    .map(__payment => PAYMENT_ID_TO_SLUG[__payment.id])
    .filter(Boolean);

  if (!_accepted.length) return PAYMENT_METHOD_OPTIONS;

  return PAYMENT_METHOD_OPTIONS.filter(
    __option => _accepted.includes(__option.value) || (!!keepValue && __option.value === keepValue),
  );
}

/** Contas bancárias do fornecedor no formato de options do Slct. */
export function supplierAccountOptions(supplier) {
  return (supplier?.account || []).map(__account => ({
    value: __account.id,
    label: accountLabel(__account),
  }));
}

/** Chaves PIX do fornecedor no formato de options do Slct. */
export function supplierPixOptions(supplier) {
  return (supplier?.pixKeys || []).map(__pix => ({
    value: __pix.id,
    label: pixLabel(__pix),
  }));
}

/**
 * Fornecedor sem nada cadastrado continua mostrando o select, com uma única
 * opção desabilitada explicando o porquê — esconder o campo faria parecer que
 * a interface está quebrada.
 *
 * O valor sentinela existe para o campo continuar aparecendo vazio: com
 * `value: null` a opção casaria com o estado vazio e a mensagem apareceria
 * como se fosse uma escolha do gestor, em vez de só dentro da lista.
 */
export function withEmptyOption(options, message) {
  return options.length ? options : [{ value: "__sem_cadastro__", label: message, disabled: true }];
}

export const NO_ACCOUNTS_LABEL = "Esse fornecedor não possui conta bancária cadastrada";
export const NO_PIX_KEYS_LABEL = "Esse fornecedor não possui chave PIX cadastrada";

export function pixLabel(pix) {
  const _type = PIX_KEY_TYPE_LABEL[pix?.key_type];
  return [_type, formatPixKey(pix?.key_type, pix?.pix_key)].filter(Boolean).join(": ");
}

/**
 * CPF/CNPJ/telefone são gravados sem formatação (só dígitos, como os
 * componentes PatternFormat emitem e como os bancos aceitam colar); a máscara
 * existe apenas para leitura. Serve tanto para chave PIX quanto para o
 * documento do titular da conta bancária.
 */
export function formatDocument(type, value) {
  if (!value) return value;
  const _digits = String(value).replace(/\D/g, "");

  if (type === "cpf" && _digits.length === 11) {
    return _digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }
  if (type === "cnpj" && _digits.length === 14) {
    return _digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  }
  if (type === "phone" && _digits.length === 11) {
    return _digits.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  }
  if (type === "phone" && _digits.length === 10) {
    return _digits.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
  }
  return String(value);
}

export const formatPixKey = formatDocument;

/** Se o título ou a parcela tem algum dado de pagamento registrado para exibir. */
export function hasPaymentDetails(payment) {
  return !!(payment?.payment_pix_key || payment?.payment_bank || payment?.payment_account);
}

export function accountLabel(account) {
  return [
    account?.bank ? `Banco ${account.bank}` : null,
    account?.agency ? `Ag. ${account.agency}` : null,
    account?.account ? `${account?.type === "cp" ? "CP" : "CC"} ${account.account}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}
